import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { makeStyles } from '@material-ui/styles'
import Modal from 'core/elements/modal/Modal'
import Text from 'core/elements/Text'
import Input from 'core/elements/input/Input'
import Theme from 'core/themes/model'
import { VMHABannerState, VMHAHostStatus, VMHATaskDetail, VMHATaskPhase } from '../vmha/types'
import AccordionProgressList from 'core/components/accordion/AccordionProgressList'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Button from 'core/elements/button'
import ApiClient from 'api-client/ApiClient'
import Progress from 'core/components/progress/Progress'
import { useSelector } from 'react-redux'
import { listAllVirtualMachinesLite } from '../../../vms/actions'
import useListAction from 'core/hooks/useListAction'
import { allVirtualMachinesLiteSelectorById } from '../../../vms/selectors'
import Alert from 'core/components/Alert'
import { useVMHAPolling } from '../vmha'
import IconButton from 'core/elements/button/IconButton'
import SimpleLink from 'core/components/SimpleLink'

interface VMHADetailsDrawerProps {
  open: boolean
  onClose: () => void
  vmhaStatus?: VMHAHostStatus
  loading?: boolean
  afterRetry?: (hostId: string) => void
}

export const getStatusLabel = (phase: VMHATaskPhase): string => {
  switch (phase) {
    case VMHATaskPhase.Completed:
      return 'Completed'
    case VMHATaskPhase.Error:
      return 'Error'
    case VMHATaskPhase.Skipped:
      return 'Skipped'
    case VMHATaskPhase.InProgress:
      return 'In Progress'
    case VMHATaskPhase.Queued:
      return 'Queued'
    default:
      return phase
  }
}

export const getVHMATaskStatusClass = (phase: VMHATaskPhase, classes: any): string => {
  switch (phase) {
    case VMHATaskPhase.Completed:
      return classes.successIcon
    case VMHATaskPhase.Error:
      return classes.errorIcon
    case VMHATaskPhase.Skipped:
      return classes.warningIcon
    case VMHATaskPhase.InProgress:
    case VMHATaskPhase.Queued:
      return classes.activeIconQueued
    default:
      return ''
  }
}

export const getVHMATaskIcon = (phase: VMHATaskPhase): string => {
  switch (phase) {
    case VMHATaskPhase.Completed:
      return 'check'
    case VMHATaskPhase.Error:
      return 'times'
    case VMHATaskPhase.Skipped:
      return 'exclamation'
    case VMHATaskPhase.InProgress:
      return 'sync'
    case VMHATaskPhase.Queued:
      return 'circle'
    default:
      return phase
  }
}

export const getVMHAEventStatusClass = (phase: VMHABannerState, classes: any): string => {
  switch (phase) {
    case VMHABannerState.Completed:
      return classes.successIcon
    case VMHABannerState.Error:
      return classes.errorIcon
    case VMHABannerState.Warning:
      return classes.warningIcon
    case VMHABannerState.InProgress:
      return classes.activeIcon
    default:
      return ''
  }
}

const getVMHAEventIconName = (phase: VMHABannerState): string => {
  switch (phase) {
    case VMHABannerState.Completed:
      return 'circle-check'
    case VMHABannerState.InProgress:
      return 'sync'
    case VMHABannerState.Warning:
      return 'exclamation-circle'
    case VMHABannerState.Error:
      return 'times-circle'
    default:
      return phase
  }
}

export const VMHAErrorStates = [VMHABannerState.Error, VMHABannerState.Warning]
const RETRY_SUCCESS_ALERT_TIMEOUT = 60 * 1000

const { haMgr } = ApiClient.getInstance()

// Sort the tasks based on task phase
const sortTasksAsPerPhase = (tasks: VMHATaskDetail[]): VMHATaskDetail[] => {
  return tasks.sort((a, b) => {
    const getSortPriority = (phase: VMHATaskPhase): number => {
      if (phase === VMHATaskPhase.InProgress) return 0
      if (phase === VMHATaskPhase.Queued) return 1
      if (phase === VMHATaskPhase.Completed) return 2
      if (phase === VMHATaskPhase.Error) return 3
      if (phase === VMHATaskPhase.Skipped) return 4
      return 5
    }

    return getSortPriority(a.phase) - getSortPriority(b.phase)
  })
}

const VMHADetailsDrawer: React.FC<VMHADetailsDrawerProps> = ({
  open,
  onClose,
  vmhaStatus,
  loading = false,
  afterRetry,
}) => {
  const classes = useStyles()
  const [searchQuery, setSearchQuery] = useState('')
  const [retrying, setRetrying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { reloadHostVMHAStatus, reloading: reloadVMHAStatus } = useVMHAPolling()

  // List All VMs to show VM names in the tasks
  useListAction(listAllVirtualMachinesLite)
  const allVms = useSelector(allVirtualMachinesLiteSelectorById)

  // Search tasks based on search query
  const filteredTasks = useMemo(() => {
    if (!searchQuery) {
      return sortTasksAsPerPhase(vmhaStatus?.tasks || [])
    }

    const searchQueryLowerCased = searchQuery.toLowerCase()
    const tasks = vmhaStatus?.tasks.filter(
      (task) =>
        allVms(task.vmId)
          ?.name.toLowerCase()
          .includes(searchQueryLowerCased) ||
        task.phase?.toLowerCase().includes(searchQueryLowerCased),
    )

    return sortTasksAsPerPhase(tasks)
  }, [vmhaStatus?.tasks, searchQuery, allVms])

  // Filter failed VMs
  const failedVMs = useMemo(
    () =>
      filteredTasks?.filter(
        (task) => task.phase === VMHATaskPhase.Error || task.phase === VMHATaskPhase.Skipped,
      ) || [],
    [filteredTasks],
  )

  // Hide retry success after RETRY_SUCCESS_ALERT_TIMEOUT
  const showRetrySuccessRef = useRef<boolean>(false)
  const showRetrySuccessTimeoutRef = useRef<NodeJS.Timeout>(null)
  useEffect(() => {
    if (showRetrySuccessRef?.current) {
      showRetrySuccessTimeoutRef?.current && clearTimeout(showRetrySuccessTimeoutRef?.current)
      showRetrySuccessTimeoutRef.current = setTimeout(
        () => (showRetrySuccessRef.current = false),
        RETRY_SUCCESS_ALERT_TIMEOUT,
      )
    }

    // Cleanup
    return () => {
      showRetrySuccessRef.current = false
      showRetrySuccessTimeoutRef.current && clearTimeout(showRetrySuccessTimeoutRef?.current)
    }
  }, [showRetrySuccessRef])

  // Reload VMHA statuses for the host
  const reload = useCallback(() => {
    reloadHostVMHAStatus(vmhaStatus?.hostId)
    afterRetry?.(vmhaStatus?.hostId)
  }, [vmhaStatus?.hostId, afterRetry, reloadHostVMHAStatus])

  const handleRetryEvacuation = useCallback(
    async (e: React.MouseEvent, vmId?: string) => {
      e.stopPropagation()
      // If no specific VM ID is provided, we can only retry if there are failed VMs
      if (!vmId && !failedVMs?.length) {
        return
      }

      try {
        setRetrying(true)
        const response = await haMgr.retryVMHA({
          vmha_uuid: vmhaStatus?.event.vmhaUuid,
          host_id: vmhaStatus?.hostId,
          vm_list: vmId ? [vmId] : failedVMs.map((vm) => vm.vmId),
          az: vmhaStatus?.event.cluster,
        })

        if (response?.phase === 'triggered') {
          showRetrySuccessRef.current = true
          reload()
        } else {
          setError(response?.reason || 'Error occurred when triggering retry')
        }
      } catch (err) {
        setError('Failed to trigger retry')
      } finally {
        setRetrying(false)
      }
    },
    [vmhaStatus, failedVMs],
  )

  const renderStatus = (task: VMHATaskDetail) => {
    return (
      <>
        <FontAwesomeIcon
          className={`${classes.listIcon} ${getVHMATaskStatusClass(task.phase, classes)}`}
          solid
          spin={task.phase === VMHATaskPhase.InProgress}
        >
          {task.phase ? getVHMATaskIcon(task.phase) : task.task}
        </FontAwesomeIcon>
        <Text variant="body2">{task.phase ? getStatusLabel(task.phase) : task?.task}</Text>
      </>
    )
  }

  const renderName = useCallback(
    (task: VMHATaskDetail) => {
      const vm = allVms(task.vmId)
      return <Text variant="body2">{vm?.name || task.vmId}</Text>
    },
    [allVms],
  )

  const renderDetails = (task: VMHATaskDetail) => {
    return (
      <>
        {/* Skipped or Queued tasks show warning message */}
        {[VMHATaskPhase.Skipped, VMHATaskPhase.Error].includes(task.phase) ? (
          <div className={classes.messageSection}>
            <Text variant="caption1">
              <b>{task.phase === VMHATaskPhase.Skipped ? `Warning Message` : `Error Message`}</b>
            </Text>
            <Text variant="body2">
              {task.reason ||
                task.fault ||
                'The VM cannot be migrated due to some constraint. Please check the logs for more details.'}
            </Text>
            <Button
              variant="tertiary"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                handleRetryEvacuation(e, task?.vmId)
              }
              icon="refresh"
              className={classes.retryButton}
              loading={retrying}
              data-testid="vmha-details-drawer-retry-single-vm-button"
            >
              Retry Evacuation
            </Button>
          </div>
        ) : (
          <>
            {/* Completed or In Progress tasks show VM details */}
            <div className={classes.vmDetails} style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div className={classes.detailItem}>
                <Text variant="caption1">VM UUID</Text>
                <Text variant="body2">{task.vmId || 'N/A'}</Text>
              </div>
              <div className={classes.detailItem}>
                <Text variant="caption1">VM State</Text>
                <Text variant="body2">{task.vmState || 'N/A'}</Text>
              </div>
            </div>
          </>
        )}
      </>
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="VMHA Event Details"
      panel="drawer"
      slideFrom="right"
      maxWidth={800}
      footer={
        <>
          {error && <Alert variant="error" message={error} />}
          <Button
            variant="secondary"
            onClick={onClose}
            data-testid="vmha-details-drawer-close-button"
          >
            Close
          </Button>
          {failedVMs.length > 0 && (
            <Button
              variant="primary"
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleRetryEvacuation(event)}
              data-testid="vmha-details-drawer-retry-all-button"
              loading={retrying}
            >
              Retry Evacuation ({failedVMs.length} VMs)
            </Button>
          )}
        </>
      }
    >
      <Progress loading={loading || reloadVMHAStatus}>
        {showRetrySuccessRef.current && (
          <Alert
            className={classes.retrySuccessAlert}
            variant="success"
            message={
              <Text variant="body2">
                Evacuation retriggered successfully. The updated status may take a few moments to
                appear.{' '}
                <SimpleLink bold icon="refresh" onClick={reload}>
                  Refresh
                </SimpleLink>
              </Text>
            }
          />
        )}
        {vmhaStatus ? (
          <div className={classes.container}>
            <Text variant="body2">
              <strong>Please note:</strong> We have detected a failure on
              <strong> {vmhaStatus.event.instanceName} </strong> host. As a result, all virtual
              machines (VMs) running on the affected host will attempt to migrate automatically to a
              healthy host in the environment.
              <ul className={classes.unorderedList}>
                <li>
                  VMs currently in <strong>"error"</strong>, <strong>"unknown"</strong>,{' '}
                  <strong>"rescued"</strong> or <strong>"resized"</strong> status will be skipped
                  during the migration process.
                </li>
                <li>
                  All other VMs in a valid operational state will be included in the migration
                  attempt.
                </li>
              </ul>
            </Text>
            {/* Event Information Section */}
            <div className={classes.infoGrid}>
              <Text variant="body2" className={classes.label}>
                VM evacuation status
              </Text>
              <Text variant="caption1" className={classes.value}>
                <FontAwesomeIcon
                  className={getVMHAEventStatusClass(vmhaStatus.state, classes)}
                  solid
                  spin={vmhaStatus.state === VMHABannerState.InProgress}
                >
                  {getVMHAEventIconName(vmhaStatus.state)}
                </FontAwesomeIcon>
                {vmhaStatus.state}
              </Text>
              <Text variant="body2" className={classes.label}>
                Number of VMs on host
              </Text>
              <Text variant="caption1" className={classes.value}>
                {vmhaStatus.event.totalVms}
              </Text>
              {failedVMs?.length > 0 && (
                <>
                  <Text variant="body2" className={classes.label}>
                    Number of VMs unable to evacuate
                  </Text>
                  <Text variant="caption1" className={classes.value}>
                    {failedVMs?.length}
                  </Text>
                </>
              )}
              <Text variant="body2" className={classes.label}>
                Error occurrence time
              </Text>
              <Text variant="caption1" className={classes.value}>
                {vmhaStatus.eventOccurrenceTime}
              </Text>
              {VMHAErrorStates.includes(vmhaStatus.state) && vmhaStatus.event.reason && (
                <>
                  <Text variant="body2" className={classes.label}>
                    Error occurrence time
                  </Text>
                  <Text variant="caption1" className={classes.value}>
                    {vmhaStatus.event.reason}
                  </Text>
                </>
              )}
            </div>

            {/* VM Tasks Section */}
            <div>
              <div className={classes.listHeader}>
                <div className={classes.headerLeft}>
                  <Text variant="body1">
                    <b>Virtual Machines</b> Recovery Migration
                  </Text>
                  <Text variant="body2">
                    {vmhaStatus.tasks.filter((t) => t.phase === VMHATaskPhase.Completed).length} of{' '}
                    {vmhaStatus.tasks.length} VMs attempting to evacuate
                  </Text>
                </div>
                <div className={classes.searchContainer}>
                  <Input
                    className={classes.searchInput}
                    placeholder="Search status or VM"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    icon="search"
                    iconProps={{ placement: 'start' }}
                  />
                  <IconButton
                    className={classes.refreshButton}
                    icon="refresh"
                    onClick={reload}
                    disabled={loading}
                    info="Refresh"
                  />
                </div>
              </div>
              <div className={classes.columnHeaders}>
                <Text variant="body2" className={classes.columnHeader}>
                  Status
                </Text>
                <div />
                <Text variant="body2" className={classes.columnHeader}>
                  VM Name
                </Text>
                <div />
              </div>
              <AccordionProgressList
                items={filteredTasks}
                uniqueIdentifier="vmId"
                renderStatus={renderStatus}
                renderName={renderName}
                renderDetails={renderDetails}
              />
            </div>
          </div>
        ) : (
          <Text variant="body2">VMHA data is not available at the moment.</Text>
        )}
      </Progress>
    </Modal>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: 'grid',
    gap: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  unorderedList: {
    paddingLeft: theme.spacing(3),
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2, 0),
    marginBottom: theme.spacing(2),
  },
  label: {
    fontWeight: 500,
    color: theme.palette.grey[700],
  },
  value: {
    color: theme.palette.grey[900],
    textTransform: 'capitalize',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  listIcon: {
    fontSize: 16,
  },
  successIcon: {
    color: theme.palette.green[700],
  },
  errorIcon: {
    color: theme.palette.red[700],
  },
  warningIcon: {
    color: theme.palette.yellow[900],
  },
  activeIcon: {
    color: theme.palette.blue[700],
  },
  activeIconQueued: {
    fontSize: 10,
    color: theme.palette.grey[300],
  },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: '4px 4px 0 0',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderBottom: 'none',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  searchInput: {
    width: 200,
  },
  refreshButton: {
    marginTop: theme.spacing(1),
  },
  columnHeaders: {
    display: 'grid',
    gridTemplateColumns: '116px 1px auto 32px',
    gap: '0px 16px',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderBottom: 'none',
    fontWeight: 600,
  },
  columnHeader: {
    fontWeight: 600,
    color: theme.palette.grey[700],
  },
  retryButton: {
    marginTop: theme.spacing(2),
  },
  vmDetails: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  messageSection: {
    padding: theme.spacing(0.5, 1),
  },
  retrySuccessAlert: {
    marginBottom: theme.spacing(2),
  },
}))

export default VMHADetailsDrawer
